import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../api';
import { useNotificationsStore } from '../store';

export const notificationsKeys = {
  listRoot: (restaurantId: string) => ['notifications', restaurantId] as const,
  list: (restaurantId: string, unreadOnly: boolean) =>
    ['notifications', restaurantId, { unreadOnly }] as const,
  preferences: (restaurantId: string) => ['notification-preferences', restaurantId] as const,
};

export function useMarkRead(restaurantId: string | undefined) {
  const queryClient = useQueryClient();
  const markOneRead = useNotificationsStore((s) => s.markOneRead);
  return useMutation({
    mutationFn: (notificationId: string) => {
      if (!restaurantId) throw new Error('No restaurant');
      return notificationsApi.markRead(restaurantId, notificationId);
    },
    onMutate: (notificationId) => {
      markOneRead(notificationId);
    },
    onSettled: () => {
      if (restaurantId) {
        queryClient.invalidateQueries({ queryKey: notificationsKeys.listRoot(restaurantId) });
      }
    },
  });
}

export function useMarkAllRead(restaurantId: string | undefined) {
  const queryClient = useQueryClient();
  const markAllReadLocal = useNotificationsStore((s) => s.markAllReadLocal);
  return useMutation({
    mutationFn: () => {
      if (!restaurantId) throw new Error('No restaurant');
      return notificationsApi.markAllRead(restaurantId);
    },
    onMutate: () => {
      const snapshot = useNotificationsStore.getState();
      markAllReadLocal();
      return { prevItems: snapshot.items, prevUnread: snapshot.unreadCount };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      useNotificationsStore.setState({ items: ctx.prevItems, unreadCount: ctx.prevUnread });
    },
    onSettled: () => {
      if (restaurantId) {
        queryClient.invalidateQueries({ queryKey: notificationsKeys.listRoot(restaurantId) });
      }
    },
  });
}

export function useDeleteNotification(restaurantId: string | undefined) {
  const queryClient = useQueryClient();
  const removeOne = useNotificationsStore((s) => s.removeOne);
  return useMutation({
    mutationFn: (notificationId: string) => {
      if (!restaurantId) throw new Error('No restaurant');
      return notificationsApi.deleteNotification(restaurantId, notificationId);
    },
    onMutate: (notificationId) => {
      const snapshot = useNotificationsStore.getState();
      removeOne(notificationId);
      return { prevItems: snapshot.items, prevUnread: snapshot.unreadCount };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      useNotificationsStore.setState({ items: ctx.prevItems, unreadCount: ctx.prevUnread });
    },
    onSettled: () => {
      if (restaurantId) {
        queryClient.invalidateQueries({ queryKey: notificationsKeys.listRoot(restaurantId) });
      }
    },
  });
}

export function useDeleteAllNotifications(restaurantId: string | undefined) {
  const queryClient = useQueryClient();
  const clearAll = useNotificationsStore((s) => s.clearAll);
  return useMutation({
    mutationFn: () => {
      if (!restaurantId) throw new Error('No restaurant');
      return notificationsApi.deleteAllNotifications(restaurantId);
    },
    onMutate: () => {
      const snapshot = useNotificationsStore.getState();
      clearAll();
      return { prevItems: snapshot.items, prevUnread: snapshot.unreadCount };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      useNotificationsStore.setState({ items: ctx.prevItems, unreadCount: ctx.prevUnread });
    },
    onSettled: () => {
      if (restaurantId) {
        queryClient.invalidateQueries({ queryKey: notificationsKeys.listRoot(restaurantId) });
      }
    },
  });
}
